// ResultsScreen.jsx — fully dynamic: score, domains, risk factors all from scoreUtils
import React, { useMemo } from 'react';
import styles from '../../styles/assessments.module.css';
import { ChevronLeft, ChevronRight } from './SharedUI';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  computeDomains,
  computeRiskFactors,
  getAssessmentMeta,
} from '../../utils/scoreUtils';
import { useNavigate } from 'react-router-dom';
import { buildPdfHtml, getLogoBase64 } from './ViewSummary';


// ─────────────────────────────────────────────────────────
// Circular Score Ring
// ─────────────────────────────────────────────────────────
const CircularScore = ({ value, max = 100, color, trackColor, label, sub }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(value, max) / max) * circ;
  return (
    <div className={styles.circularWrap}>
      <svg width="88" height="88" viewBox="0 0 88 88" className={styles.circularSvg}>
        <circle cx="44" cy="44" r={r} fill="none" stroke={trackColor} strokeWidth="7" />
        <circle
          cx="44" cy="44" r={r} fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className={styles.circularInner}>
        <span className={styles.circularValue} style={{ color }}>{value}</span>
        {max !== 100 && <span className={styles.circularMax}>/{max}</span>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Scoring Guide (static reference table)
// ─────────────────────────────────────────────────────────
const SCORING_GUIDE = [
  {
    icon: '🌡️', title: 'Severity Score', sub: '(0–100)',
    rows: [
      ['0–20', '#059669', 'Low'],
      ['21–40', '#16A34A', 'Mild'],
      ['41–60', '#D97706', 'Moderate'],
      ['61–80', '#DC2626', 'High'],
      ['81–100', '#7F1D1D', 'Severe'],
    ],
  },
  {
    icon: '🛡️', title: 'Risk Score', sub: '(0–100)',
    rows: [
      ['0–14', '#059669', 'Low'],
      ['15–34', '#D97706', 'Moderate'],
      ['35–59', '#DC2626', 'High'],
      ['60+', '#7C2D12', 'Very High'],
      ['Red flag', '#DC2626', 'Urgent'],
    ],
  },
  {
    icon: '⭐', title: 'Care Priority', sub: '(0–100)',
    rows: [
      ['0–29', '#059669', 'Low'],
      ['30–49', '#D97706', 'Moderate'],
      ['50–69', '#DC2626', 'High'],
      ['70+', '#7C2D12', 'Very High'],
    ],
  },
];

// ─────────────────────────────────────────────────────────
// Static fallback configs for legacy 4 types
// (menopause / pcos / pregnancy / menstrual have no scoreEngine)
// ─────────────────────────────────────────────────────────
const LEGACY_CONFIG = {
  menopause: {
    title: 'Menopause Assessment Results',
    subtitle: "Here's your personalized menopause assessment summary.",
    nextSteps: ['Book a Menopause Specialist Consultation', 'Discuss treatment options', 'Start personalized care plan', 'Lifestyle & symptom management'],
    bookLabel: 'Book Specialist Consult',
    symptomScore: 68, qolScore: 62, clinicalRisk: 35, scoreColor: '#5B21B6', scoreLabel: 'Moderate',
    carePriority: 'HIGH',
    domains: [
      { icon: '🌡️', bg: '#FEF3C7', name: 'Vasomotor Symptoms', score: 78, lvl: 'High', lvlColor: '#DC2626', barColor: '#DC2626' },
      { icon: '🌙', bg: '#EDE9FE', name: 'Sleep & Fatigue', score: 72, lvl: 'High', lvlColor: '#DC2626', barColor: '#5B21B6' },
      { icon: '🧠', bg: '#FEE2E2', name: 'Psychological / Cognitive', score: 65, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '💧', bg: '#DBEAFE', name: 'Urogenital Symptoms', score: 55, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🦴', bg: '#D1FAE5', name: 'Bone & Joint Symptoms', score: 45, lvl: 'Mild', lvlColor: '#059669', barColor: '#059669' },
      { icon: '❤️', bg: '#FFE4E6', name: 'Quality of Life Impact', score: 62, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#DC2626' },
    ],
    riskFactors: [
      { icon: '📅', bg: '#EDE9FE', name: 'Age (51 years)', val: '+10' },
      { icon: '🩺', bg: '#FFE4E6', name: 'Family history of breast cancer', val: '+10' },
      { icon: '❤️', bg: '#DBEAFE', name: 'High blood pressure', val: '+10' },
      { icon: '🏃', bg: '#D1FAE5', name: 'Low physical activity', val: '+5' },
      { icon: '😓', bg: '#FEF9C3', name: 'High stress level', val: '+5' },
      { icon: '💊', bg: '#EDE9FE', name: 'Hormone therapy (not using)', val: '0' },
    ],
  },
  pcos: {
    title: 'PCOS Assessment Results',
    subtitle: "Here's your personalized PCOS screening summary.",
    nextSteps: ['Consult a gynecologist / endocrinologist', 'Get hormonal blood panel done', 'Pelvic ultrasound screening', 'Begin PCOS lifestyle program'],
    bookLabel: 'Book Specialist Consult',
    symptomScore: 65, qolScore: 55, clinicalRisk: 48, scoreColor: '#7C3AED', scoreLabel: 'Moderate',
    carePriority: 'HIGH',
    domains: [
      { icon: '📅', bg: '#FEF3C7', name: 'Menstrual Irregularity', score: 70, lvl: 'High', lvlColor: '#DC2626', barColor: '#DC2626' },
      { icon: '⚖️', bg: '#EDE9FE', name: 'Weight & Metabolism', score: 55, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🧬', bg: '#FEE2E2', name: 'Hormonal Symptoms', score: 60, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🌱', bg: '#D1FAE5', name: 'Fertility Indicators', score: 40, lvl: 'Mild', lvlColor: '#059669', barColor: '#059669' },
      { icon: '🏃', bg: '#DBEAFE', name: 'Lifestyle Factors', score: 45, lvl: 'Mild', lvlColor: '#059669', barColor: '#059669' },
    ],
    riskFactors: [
      { icon: '📅', bg: '#FEF3C7', name: 'Irregular cycles', val: '+15' },
      { icon: '⚖️', bg: '#EDE9FE', name: 'Weight gain / difficulty losing', val: '+10' },
      { icon: '🧬', bg: '#FEE2E2', name: 'Hormonal symptoms (acne/hair)', val: '+10' },
      { icon: '🩺', bg: '#D1FAE5', name: 'No prior PCOS diagnosis', val: '+5' },
      { icon: '😓', bg: '#FEF9C3', name: 'High stress / poor sleep', val: '+5' },
    ],
  },
  pregnancy: {
    title: 'Pregnancy Assessment Results',
    subtitle: "Here's your prenatal health assessment summary.",
    nextSteps: ['Schedule next antenatal visit', 'Complete recommended blood tests', 'Join prenatal care program', 'Nutritional guidance consultation'],
    bookLabel: 'Book Prenatal Consult',
    symptomScore: 55, qolScore: 70, clinicalRisk: 28, scoreColor: '#6D28D9', scoreLabel: 'Low-Moderate',
    carePriority: 'MODERATE',
    domains: [
      { icon: '🤰', bg: '#FEF3C7', name: 'General Pregnancy Health', score: 72, lvl: 'Good', lvlColor: '#059669', barColor: '#059669' },
      { icon: '❤️', bg: '#FFE4E6', name: 'Medical Risk Factors', score: 40, lvl: 'Low', lvlColor: '#059669', barColor: '#059669' },
      { icon: '🥗', bg: '#D1FAE5', name: 'Nutrition & Supplements', score: 65, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🧘', bg: '#DBEAFE', name: 'Psychosocial Wellbeing', score: 70, lvl: 'Good', lvlColor: '#059669', barColor: '#059669' },
      { icon: '🏥', bg: '#EDE9FE', name: 'Antenatal Care Coverage', score: 55, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
    ],
    riskFactors: [
      { icon: '🤰', bg: '#FEF3C7', name: 'First pregnancy', val: '+5' },
      { icon: '💊', bg: '#EDE9FE', name: 'Taking folic acid', val: '0' },
      { icon: '🏃', bg: '#D1FAE5', name: 'Regular physical activity', val: '0' },
      { icon: '😓', bg: '#FEF9C3', name: 'Mild stress / anxiety', val: '+5' },
    ],
  },
  menstrual: {
    title: 'Menstrual Health Assessment Results',
    subtitle: "Here's your menstrual disorder screening summary.",
    nextSteps: ['Consult a gynecologist', 'Hormonal blood panel', 'Pelvic ultrasound if needed', 'Cycle tracking & lifestyle changes'],
    bookLabel: 'Book Gynecologist Consult',
    symptomScore: 62, qolScore: 50, clinicalRisk: 40, scoreColor: '#4C1D95', scoreLabel: 'Moderate',
    carePriority: 'HIGH',
    domains: [
      { icon: '📅', bg: '#FEF3C7', name: 'Cycle Regularity', score: 65, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🩸', bg: '#FFE4E6', name: 'Bleeding Pattern', score: 70, lvl: 'High', lvlColor: '#DC2626', barColor: '#DC2626' },
      { icon: '😣', bg: '#FEE2E2', name: 'Pain & Discomfort', score: 55, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🧬', bg: '#EDE9FE', name: 'Hormonal Indicators', score: 60, lvl: 'Moderate', lvlColor: '#D97706', barColor: '#D97706' },
      { icon: '🧘', bg: '#D1FAE5', name: 'Lifestyle Factors', score: 40, lvl: 'Mild', lvlColor: '#059669', barColor: '#059669' },
    ],
    riskFactors: [
      { icon: '📅', bg: '#FEF3C7', name: 'Irregular cycles', val: '+10' },
      { icon: '🩸', bg: '#FFE4E6', name: 'Heavy / prolonged bleeding', val: '+10' },
      { icon: '😣', bg: '#FEE2E2', name: 'Significant menstrual pain', val: '+10' },
      { icon: '😓', bg: '#FEF9C3', name: 'High stress / poor sleep', val: '+5' },
    ],
  },
};

// ─────────────────────────────────────────────────────────
// Care Priority from risk percent
// ─────────────────────────────────────────────────────────
const carePriorityFromPercent = (pct) => {
  if (pct <= 20) return { label: 'LOW', color: '#059669' };
  if (pct <= 45) return { label: 'MODERATE', color: '#D97706' };
  if (pct <= 70) return { label: 'HIGH', color: '#DC2626' };
  return { label: 'URGENT', color: '#7F1D1D' };
};

// QoL label from percent
const qolLabelFromPercent = (pct) => {
  if (pct <= 25) return { label: 'Minimal Impact', color: '#059669' };
  if (pct <= 50) return { label: 'Mild Impact', color: '#16A34A' };
  if (pct <= 70) return { label: 'Moderate Impact', color: '#D97706' };
  if (pct <= 85) return { label: 'High Impact', color: '#DC2626' };
  return { label: 'Severe Impact', color: '#7F1D1D' };
};

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────

const IconDot = ({ emoji, bg }) => (
  <span className={styles.iconDot} style={{ background: bg }}>{emoji}</span>
);

const DomainRow = ({ d }) => (
  <div className={styles.resultsDomainRow}>
    <div className={styles.resultsDomainIcon} style={{ background: d.bg }}>{d.icon}</div>
    <span className={styles.resultsDomainName}>{d.name}</span>
    <div className={styles.resultsProgressTrack}>
      <div
        className={styles.resultsProgressFill}
        style={{ width: `${d.score}%`, background: d.barColor }}
      />
    </div>
    <span className={styles.resultsDomainScore}>{d.score}</span>
    <span className={styles.resultsDomainLevel} style={{ color: d.lvlColor }}>{d.lvl}</span>
  </div>
);

// ─────────────────────────────────────────────────────────
// Main ResultsScreen
// ─────────────────────────────────────────────────────────

const ResultsScreen = ({ answers = {}, assessmentType = 'menopause', onRestart, onBack, scoreResult, goTo }) => {
  const navigate = useNavigate();
  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    }
  };
  const hasRealScore = scoreResult && typeof scoreResult.raw === 'number';
  const isLegacy = ['menopause', 'pcos', 'pregnancy', 'menstrual'].includes(assessmentType);
  const meta = getAssessmentMeta(assessmentType);

  // ── Dynamic values (scored assessments) ─────────────────
  const dynamicDomains = useMemo(() => computeDomains(assessmentType, answers), [assessmentType, answers]);
  const dynamicRiskFactors = useMemo(() => computeRiskFactors(assessmentType, answers), [assessmentType, answers]);

  // ── Pick config source ──────────────────────────────────
  const legacy = isLegacy ? (LEGACY_CONFIG[assessmentType] || LEGACY_CONFIG.menopause) : null;

  const title = isLegacy ? legacy.title : `${meta.icon} ${meta.label} Assessment Results`;
  const subtitle = isLegacy ? legacy.subtitle : "Here's your personalized assessment summary based on your responses.";
  const nextSteps = isLegacy ? legacy.nextSteps : ['Consult a healthcare professional', 'Review your personalized care plan', 'Schedule a follow-up appointment', 'Discuss results with your doctor'];
  const bookLabel = isLegacy ? legacy.bookLabel : 'Book a Consultation';
  const domains = isLegacy ? legacy.domains : dynamicDomains;
  const riskFactors = isLegacy ? legacy.riskFactors : dynamicRiskFactors;

  // ── Score values ────────────────────────────────────────
  const symptomScore = hasRealScore
    ? scoreResult.percent
    : (legacy?.symptomScore ?? 60);

  const clinicalRisk = hasRealScore
    ? scoreResult.percent
    : (legacy?.clinicalRisk ?? 35);

  // QoL: for legacy menopause try answers.s10.overallQol
  const rawQol = answers?.s10?.overallQol;
  const qolScore = rawQol != null
    ? Math.round((rawQol / 10) * 100)
    : (hasRealScore ? Math.round(scoreResult.percent * 0.85) : (legacy?.qolScore ?? 60));

  const scoreColor = hasRealScore ? scoreResult.color : (legacy?.scoreColor ?? meta.color ?? '#5B21B6');
  const scoreLabel = hasRealScore ? scoreResult.label : (legacy?.scoreLabel ?? 'Moderate');

  const carePriority = isLegacy
    ? (legacy?.carePriority ?? 'HIGH')
    : carePriorityFromPercent(clinicalRisk).label;
  const carePriorityColor = hasRealScore ? scoreResult.color : (legacy ? (LEGACY_CONFIG[assessmentType]?.scoreColor ?? '#DC2626') : carePriorityFromPercent(clinicalRisk).color);

  const qolObj = qolLabelFromPercent(qolScore);

  const totalRiskScore = hasRealScore
    ? `${scoreResult.raw} / ${scoreResult.max}`
    : `${clinicalRisk} / 100`;


  const handleDownloadReport = async () => {
    if (!answers || Object.keys(answers).length === 0) {
      alert("No assessment data available to download.");
      return;
    }

    try {
      const logoBase64 = await getLogoBase64();

      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:fixed;left:-9999px;top:0;z-index:-1'; // ← same as DynamicSummary
      document.body.appendChild(tempDiv);

      const reportHTML = buildPdfHtml(
        answers,
        getPageTitle(),
        `RPT-${Date.now().toString(36).toUpperCase()}`,
        new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
        logoBase64
      );

      tempDiv.innerHTML = reportHTML;

      await new Promise(r => setTimeout(r, 150)); // ← same as DynamicSummary, let it render

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#F0F4F8'
      });

      document.body.removeChild(tempDiv);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const usableW = pdfW - margin * 2;
      const usableH = pdfH - margin * 2;
      const scale = usableW / canvas.width;
      const sliceH = usableH / scale;

      let srcY = 0;
      let page = 0;

      while (srcY < canvas.height) {
        if (page > 0) pdf.addPage();
        const h = Math.min(sliceH, canvas.height - srcY);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = h;
        slice.getContext('2d').drawImage(canvas, 0, -srcY);
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, usableW, h * scale);
        srcY += sliceH;
        page++;
      }

      pdf.save(`${getPageTitle().replace(/\s+/g, '_')}.pdf`);

    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };
  const getPageTitle = () => {
    if (assessmentType?.toLowerCase().includes('pregnancy')) return "Pregnancy Risk Assessment Report";
    if (assessmentType === 'general') return "Health Assessment Report";
    return "Assessment Report";
  };

  const getSectionTitle = (key) => {
    const titles = {
      s1: "Basic & Pregnancy Information",
      s2: "Medical History & Allergies",
      s3: "Previous Pregnancy Complications",
      s4: "Family & Genetic History",
      s5: "Current Symptoms",
      s6: "Danger Signs",
      s7: "Nutrition & Supplements",
    };
    return titles[key] || `Section ${key.toUpperCase()}`;
  };

  const formatLabel = (label) => {
    return label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  const formatValueForPDF = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ') || 'None';
    }
    return String(value);
  };
  // ─────────────────────────────────────────────────────────
  return (
    <div className={styles.resultsWrapper}>

      {/* ══ HEADER ══ */}
      <div className={styles.resultsHeader}>
        <div className={styles.resultsHeaderLeft}>
          <div  className={styles.logoInResultsScreen}>
            <img src='/Home/FemCareLogo.svg' alt='logo' />
          </div>
          <h1 className={styles.resultsTitle}>{title}</h1>
          <p className={styles.resultsSubtitle}>{subtitle}</p>
        </div>
        <div className={styles.resultsHeaderActions}>
          <button className={styles.navBack} onClick={handleBack}>
            <ChevronLeft /> Back
          </button>
          <button className={styles.navBack} onClick={() => handleDownloadReport()}>Download Report</button>
          {/* <button
            className={styles.navBack}
            onClick={() =>
              navigate('/view-results', {
                state: {
                  assessmentType,
                  answers,
                  fromResults: true
                },
              })
            }
          >
            View Report
          </button> */}
          <button
            className={styles.navBack}
            onClick={() => goTo()}
          >
            View Report
          </button>
        </div>
      </div>

      {/* ══ SCORED ASSESSMENT BANNER (new types) ══ */}
      {hasRealScore && (
        <div className={styles.scoreBanner}>
          <div className={styles.scoreBannerLeft}>
            <div className={styles.scoreBannerPill} style={{ background: scoreColor }}>
              <div className={styles.scoreBannerRaw}>{scoreResult.raw}</div>
              <div className={styles.scoreBannerMax}>/ {scoreResult.max}</div>
            </div>
            <div className={styles.scoreBannerMeta}>
              <div className={styles.scoreBannerLabel} style={{ color: scoreColor }}>
                {scoreResult.label}
              </div>
              <div className={styles.scoreBannerRanges}>
                {scoreResult.ranges.map((r) => (
                  <span
                    key={r.label}
                    className={styles.scoreBannerRange}
                    style={{
                      background: r.color + '18',
                      color: r.color,
                      border: scoreResult.label === r.label
                        ? `1.5px solid ${r.color}`
                        : '1.5px solid transparent',
                    }}
                  >
                    {r.min}–{r.max}: {r.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {scoreResult.redFlagNote && (
            <div className={styles.scoreBannerFlag}>
              ⚠️ {scoreResult.redFlagNote}
            </div>
          )}
        </div>
      )}

      {/* ══ DISCLAIMER ══ */}
      <div className={styles.resultsDisclaimerWrap}>
        <div className={styles.resultsDisclaimer}>
          <div className={styles.resultsDisclaimerIcon}>i</div>
          <p className={styles.resultsDisclaimerText}>
            <strong>This assessment is for care guidance only — it is not a medical diagnosis.</strong>
            {' '}Please consult a licensed healthcare professional for clinical evaluation and treatment.
          </p>
        </div>
      </div>

      {/* ══ QUICK ACTIONS ══ */}
      <div className={styles.resultsQuickActionsWrap}>
        <div className={styles.resultsQuickActionsHeader}>
          <h3 className={styles.resultsQuickActionsTitle}>
            Recommended Next Actions
          </h3>

          <p className={styles.resultsQuickActionsSubtitle}>
            Continue your care journey with expert guidance and personalized support.
          </p>
        </div>

        <div className={styles.resultsActionsGrid}>
          {[
            {
              title: 'Book Appointment',
              desc: 'Consult a specialist',
              bg: '#F8F9F9',
              link: '/book-appointment',
              icon: '📅',
            },
            {
              title: 'Chat with Expert',
              desc: 'Guidance and support',
              bg: '#E3F2FD',
              link: '/chat',
              icon: '💬',
            },
            {
              title: 'Enroll Program',
              desc: 'Personalized care programs',
              bg: '#FEF9E7',
              link: '/programs',
              icon: '✨',
            },
          ].map((action, idx) => (
            <div
              key={idx}
              className={styles.resultsActionCard}
              style={{ background: action.bg }}
              onClick={() => action.link && navigate(action.link)}
            >
              <div className={styles.resultsActionIcon}>
                {action.icon}
              </div>

              <div className={styles.resultsActionBody}>
                <div className={styles.resultsActionTitle}>
                  {action.title}
                </div>

                <div className={styles.resultsActionDesc}>
                  {action.desc}
                </div>
              </div>

              <div className={styles.resultsActionArrow}>
                <ChevronRight />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ SCORE CARDS GRID ══ */}
      {/* <div className={styles.resultsScoreGrid}>

        <div className={styles.resultsCard}>
          <div className={styles.resultsCardLabel}>
            <IconDot emoji="🌡️" bg="#FEF3C7" /> Symptom Severity Score
          </div>
          <div className={styles.resultsCardCenter}>
            <CircularScore value={symptomScore} color={scoreColor} trackColor={scoreColor + '22'} />
            <span className={styles.resultsCardBadge} style={{ background: scoreColor + '18', color: scoreColor }}>
              {scoreLabel}
            </span>
            <span className={styles.resultsCardText}>
              {symptomScore >= 70
                ? 'Symptoms are significantly impacting your daily life.'
                : symptomScore >= 45
                ? 'Symptoms are moderately affecting your well-being.'
                : 'Symptoms are present but manageable.'}
            </span>
          </div>
        </div>


        <div className={styles.resultsCard}>
          <div className={styles.resultsCardLabel}>
            <IconDot emoji="🛡️" bg="#FFE4E6" /> Clinical Risk Score
          </div>
          <div className={styles.resultsCardCenter}>
            <CircularScore value={clinicalRisk} color="#DC2626" trackColor="#FEE2E2" />
            <span className={styles.resultsCardBadge} style={{ background: '#FEE2E2', color: '#DC2626' }}>
              {carePriorityFromPercent(clinicalRisk).label}
            </span>
            <span className={styles.resultsCardText}>
              {clinicalRisk >= 60
                ? 'Multiple risk factors need urgent medical attention.'
                : clinicalRisk >= 35
                ? 'Some risk factors require medical evaluation.'
                : 'Low clinical risk — continue healthy habits.'}
            </span>
          </div>
        </div>


        <div className={styles.resultsCard}>
          <div className={styles.resultsCardLabel}>
            <IconDot emoji="💙" bg="#DBEAFE" /> Quality of Life Score
          </div>
          <div className={styles.resultsCardCenter}>
            <CircularScore value={qolScore} color="#2563EB" trackColor="#DBEAFE" />
            <span className={styles.resultsCardBadge} style={{ background: '#DBEAFE', color: qolObj.color }}>
              {qolObj.label}
            </span>
            <span className={styles.resultsCardText}>
              {qolScore >= 70
                ? 'Symptoms are significantly affecting your daily quality of life.'
                : qolScore >= 40
                ? 'Symptoms are moderately impacting your well-being.'
                : 'Quality of life is relatively well maintained.'}
            </span>
          </div>
        </div>


        <div className={styles.resultsCard}>
          <div className={styles.resultsCardLabel}>
            <IconDot emoji="⭐" bg="#EDE9FE" /> Care Priority
          </div>
          <div className={styles.resultsCarePriority}>
            <div
              className={styles.resultsCarePriorityHigh}
              style={{ color: carePriorityColor }}
            >
              {carePriority}
            </div>
            <p className={styles.resultsCardText}>
              Your results indicate you may benefit from professional care and a personalized treatment plan.
            </p>
            <button onClick={() => navigate('/book-appointment')} className={styles.resultsPrimaryBtn} style={{ background: meta.color }}>
              {bookLabel} <ChevronRight />
            </button>
          </div>
        </div>

        <div className={styles.resultsCard}>
          <div className={styles.resultsCardLabel}>
            <IconDot emoji="🎯" bg="#D1FAE5" /> Next Best Actions
          </div>
          <div className={styles.resultsNextStepsList}>
            {nextSteps.map((item, i) => (
              <div key={i} className={styles.resultsNextStep}>
                <div className={styles.resultsNextStepCheck} style={{ background: meta.color }}>
                  <svg width="9" height="9" fill="none" stroke="white" strokeWidth={3} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* ══ BOTTOM 3-COL GRID ══ */}
      {/* <div className={styles.resultsBottomGrid}>
        <div className={styles.resultsBottomCard}>
          <div className={styles.resultsBottomTitle}>Symptom Domain Breakdown</div>
          {domains.length > 0 ? (
            <>
              <div className={styles.resultsDomainHeader}>
                <span />
                <span className={styles.resultsDomainHeaderName}>Domain</span>
                <span className={styles.resultsDomainHeaderScore}>Score</span>
                <span />
                <span className={styles.resultsDomainHeaderLevel}>Level</span>
              </div>
              {domains.map((d) => <DomainRow key={d.name} d={d} />)}
              <p className={styles.resultsDomainInfo}>
                ℹ️ Scores are calculated from your responses. Higher scores indicate greater concern.
              </p>
            </>
          ) : (
            <p className={styles.resultsCardText}>
              Domain breakdown not available for this assessment type.
            </p>
          )}
        </div>
        <div className={styles.resultsBottomCard}>
          <div className={styles.resultsBottomTitle}>Clinical Risk Factors</div>
          <div className={styles.resultsTableWrapper}>
            <table className={styles.resultsTable}>
              <thead>
                <tr>
                  <th className={styles.resultsTableHeadLeft}>Risk Factor</th>
                  <th className={styles.resultsTableHeadRight}>Impact</th>
                </tr>
              </thead>
              <tbody>
                {riskFactors.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className={styles.resultsRiskRow}>
                        <div className={styles.resultsRiskIcon} style={{ background: r.bg }}>{r.icon}</div>
                        <span className={styles.resultsRiskText}>{r.name}</span>
                      </div>
                    </td>
                    <td className={
                      r.val === 'URGENT' ? styles.resultsImpactUrgent
                      : r.val === '0'   ? styles.resultsImpactPositive
                      :                   styles.resultsImpactNegative
                    }>
                      {r.val}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className={styles.resultsTotalLabel}>Total Score</td>
                  <td className={styles.resultsTotalValue}>
                    <span className={styles.resultsTotalBadge}>{totalRiskScore}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className={styles.resultsBottomCard}>
          <div className={styles.resultsBottomTitle}>Scoring Guide</div>
          {SCORING_GUIDE.map((sg) => (
            <div key={sg.title} className={styles.resultsGuideItem}>
              <div className={styles.resultsGuideIcon}>{sg.icon}</div>
              <div className={styles.resultsGuideContent}>
                <div className={styles.resultsGuideTitle}>{sg.title}</div>
                <div className={styles.resultsGuideSub}>{sg.sub}</div>
                {sg.rows.map(([range, color, label]) => (
                  <div key={range} className={styles.resultsGuideRow}>
                    <span className={styles.resultsGuideRange}>{range}</span>
                    <span className={styles.resultsGuideLabel} style={{ color }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* <div className={styles.resultsFooter}>
        <div className={styles.resultsFooterIcon}>
          <svg width="20" height="20" fill="none" stroke={meta.color} strokeWidth="1.8" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        </div>
        <div className={styles.resultsFooterLeft}>
          <h3 className={styles.resultsFooterHeading}>What This Means For You</h3>
          <p className={styles.resultsFooterBody}>
            {symptomScore >= 60
              ? 'Your assessment shows a moderate to high symptom burden. A specialist consultation is recommended to explore treatment options and create a personalized care plan.'
              : 'Your assessment shows some symptoms that are worth monitoring. Consult a healthcare professional to review and plan next steps.'}
          </p>
        </div>
        <div className={styles.resultsFooterRight}>
          <div className={styles.resultsFooterRecommend}>Recommended Next Step</div>
          <div className={styles.resultsFooterNextStep}>
            <svg width="18" height="18" fill="none" stroke={meta.color} strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            Book a consultation with a Specialist
          </div>
          <p className={styles.resultsFooterSub}>
            Get expert guidance and a personalized care plan.
          </p>
        </div>
        <div className={styles.resultsFooterActions}>
          <button onClick={() => navigate('/book-appointment')} className={styles.resultsPrimaryBtn} style={{ background: meta.color }}>
            Book Now <ChevronRight />
          </button>
          <span onClick={() => navigate('/program')} className={styles.resultsFooterLink}>View Care Programs →</span>
        </div>
      </div> */}

      {/* <div className={styles.resultsPageFooter}>
        <span>Assessment completed · ID: HLTH-{Date.now().toString().slice(-8)}</span>
        <button className={styles.resultsRestartBtn} onClick={onRestart}>
          Take Another Assessment
        </button>
      </div> */}
    </div>
  );
};

export default ResultsScreen;
